import * as React from "react";
import {Trans, useTranslation, withTranslation} from "react-i18next";
import ActivationPageFooter from "./components/Footer/ActivationPageFooter";
import ActivationPageHeader from "./components/ActivationPageHeader";
import ActivationPageWrapper from "./components/ActivationPageWrapper";
import ActivationFormContainer from "./components/main/ActivationForm/ActivationFormContainer";
import ActivationPageMainContainer from "./components/main/ActivationPageMainContainer";
import ActivationPageSidebar from "./components/sidebar/ActivationPageSidebar";
import SidebarInfoBlock from "./components/sidebar/SidebarInfoBlock";
import SidebarInfoBlockContent from "./components/sidebar/SidebarInfoBlockContent";
import SidebarIntroBlock from "./components/sidebar/SidebarIntroBlock";
import SidebarSubscription from "./components/sidebar/SidebarSubscription";
import i18n from "i18next";
import SidebarCloudAccountContent from "./components/sidebar/SidebarCloudAccountContent";
import {themeOn, alloyDRCC} from "./config/apiConfig";
import { useFormState } from "./components/main/ActivationForm/Context/ActivationFormContext";
import { getFeatureFlag } from "./CourierService/FetchService";

const SidebarPhonebookContent: React.FC = () => {
  const { isInternalCustomerType, featureFlags } = useFormState();
  const { t } = useTranslation();

  const enableEnhancedMetadataTags = React.useMemo(
    () => getFeatureFlag("enableEnhancedMetadataTags", featureFlags),
    [featureFlags]
  );

  if (!isInternalCustomerType || !enableEnhancedMetadataTags) {
    return null;
  }

  return (
    <SidebarInfoBlockContent
      title={t('infoBlock.titlePhonebook', "About Phonebook")}
      content={
        <>
          <Trans
            i18nKey="infoBlock.contentPhonebookMain"
            components={{
              phonebookLink: <a href="https://devops.oci.oraclecorp.com/phonebook" target="_blank" rel="noopener noreferrer" aria-label="Phonebook">Phonebook</a>
            }}
          >
            <a href="https://devops.oci.oraclecorp.com/phonebook" target="_blank" rel="noopener noreferrer">Phonebook</a> is the system of record for your service team's contact and ownership details, including Service Owners, JIRA-SD queues, team Slack channels, team email, and Ocean Incident Management. A phonebook is required for all internal tenancies.
          </Trans>
          <br />
          <Trans
            i18nKey="infoBlock.contentPhonebookHelp"
            components={{
              troubleshootingGuideLink: <a href="https://confluence.oraclecorp.com/confluence/display/ACCADM/Phonebook+permissions+troubleshooting" target="_blank" rel="noopener noreferrer" aria-label="troubleshooting guide">troubleshooting guide</a>
            }}
          >
            If you are having trouble with Phonebook permissions, see the <a href="https://confluence.oraclecorp.com/confluence/display/ACCADM/Phonebook+permissions+troubleshooting" target="_blank" rel="noopener noreferrer">troubleshooting guide</a>.
          </Trans>
        </>
      }
    />
  );
};

const ActivationPage: React.FC<{}> = () => {
  const curLang = document.documentElement.lang;
  React.useEffect(() => {
    if (curLang !== i18n.language) {
      document.documentElement.lang = i18n.language
    }
  },[curLang]);
  const {t, i18n: i18nInstance} = useTranslation();
  const isEnglishTranslation = i18nInstance.language === "en";
  const homeRegionLinkComponents = isEnglishTranslation ? {
    homeRegionLink: <a href="https://docs.cloud.oracle.com/en-us/iaas/Content/Identity/Tasks/managingregions.htm?Highlight=home%20region#The" target='_blank' rel="noopener noreferrer" aria-label="Home Region">Home Region</a>
  } : undefined;

  return (
    <div data-testid="page">
      <ActivationPageHeader
        wrapperClass="brandingBar brandingHeaderColor"
        logo="oLogo"
        textClass="oLogoWords title7"
        text={t('common.cloud', "Cloud")}
      />
      <ActivationPageWrapper>
        <ActivationPageSidebar>
          <SidebarInfoBlock wrapperClass="promoContainer" >
              <SidebarPhonebookContent />
              <SidebarCloudAccountContent />
              <SidebarInfoBlockContent
                  title={t('infoBlock.titleThree', 'About Region Group')}
                  content={t('infoBlock.contentThree', 'A Region Group (also known as Realm) is a logical group of one or more OCI regions that are subject to a common set of rules for data residency, sovereignty, and governance. Region groups in OCI are also known as realms.')}
                  hide={themeOn === true}
              />
              <SidebarInfoBlockContent
                title={t('infoBlock.titleTwo', "About Regions")}
                content={!alloyDRCC ? <Trans i18nKey="infoBlock.contentTwo" components={homeRegionLinkComponents}>Your <a href="https://docs.cloud.oracle.com/en-us/iaas/Content/Identity/Tasks/managingregions.htm?Highlight=home%20region#The" target='_blank' rel="noopener noreferrer">Home Region</a> is the geographic location where your cloud account and identity resources will be created. It is not changeable after sign-up. For SaaS application(s) the Home Region does not indicate the provisioning location. Your SaaS application(s) will be provisioned in the geo-region specified on your order. After creating a cloud account to add your subscription, the Home Region is where your primary Identity Domain will be located. Depending on SaaS application the application user credentials may also be stored at the same Home Region Identity Domain location.</Trans>:
                  <Trans i18nKey="infoBlock.contentTwoAlloy" components={homeRegionLinkComponents}>Your <a href="https://docs.cloud.oracle.com/en-us/iaas/Content/Identity/Tasks/managingregions.htm?Highlight=home%20region#The" target='_blank' rel="noopener noreferrer">Home Region</a> is the geographic location where your cloud account and identity resources will be created. It is not changeable after sign-up.</Trans>
                }
              />
          </SidebarInfoBlock>
          <SidebarIntroBlock wrapperClass="progressContainer" >
            <SidebarSubscription title={t('subscriptionBlock.title', "Your Subscriptions")} />
          </SidebarIntroBlock>
        </ActivationPageSidebar>
        <ActivationPageMainContainer>
          <ActivationFormContainer />
        </ActivationPageMainContainer>
      </ActivationPageWrapper>
      <ActivationPageFooter />
    </div>
  );
};

export default withTranslation()(ActivationPage);
